import { Check } from "lucide-react";

import { Section, SectionHeading } from "./Section";
import { Reveal } from "./Reveal";
import { IsoGrid, IsoIllustration, IsoStep } from "./Iso";
import { LocaleLink } from "./LocaleLink";
import type { IndustryUseCaseSet } from "@/content/industry-usecases";
import { checkUseCaseIso } from "@/lib/iso-relevance";
import { useTx } from "@/lib/i18n-dict";

/** Dev-only signal when a card's illustration drifts away from its copy. */
function warnOnIsoMismatch(set: IndustryUseCaseSet) {
  if (!import.meta.env.DEV) return;
  for (const useCase of set.cases) {
    const result = checkUseCaseIso(useCase);
    if (!result.ok) {
      console.warn(
        `[iso-relevance] "${useCase.title}" → ${useCase.iso} scores ${result.score.toFixed(2)}; ` +
          `illustration concepts missing from the copy: ${result.missing.join(", ")}`,
      );
    }
  }
}

/**
 * "Use cases" band on an industry page — thematic cards with isometric visuals
 * so the industry reads as a set of scenarios instead of one wall of text.
 */
export function IndustryUseCases({ set: source }: { set: IndustryUseCaseSet }) {
  warnOnIsoMismatch(source);
  const set = useTx(source);

  return (
    <Section className="relative">
      <IsoGrid intensity="soft" />
      <SectionHeading eyebrow={set.eyebrow} title={set.title} lead={set.lead} />
      <div className="mt-12 grid gap-6 md:grid-cols-2">
        {set.cases.map((useCase, index) => (
          <Reveal key={useCase.title} variant="iso" delay={index * 60}>
            <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-colors hover:border-primary/50">
              <div className="relative border-b border-border bg-surface/60 p-6">
                <IsoIllustration
                  name={useCase.iso}
                  alt=""
                  className="mx-auto max-w-[16rem] transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transform-none"
                />
              </div>
              <div className="flex flex-1 flex-col p-6 md:p-8">
                <div className="flex items-start gap-4">
                  <IsoStep n={index + 1} />
                  <h3 className="font-display text-xl font-bold text-balance-tight md:text-2xl">
                    {useCase.title}
                  </h3>
                </div>
                <p className="mt-4 text-base text-muted-foreground md:text-lg">{useCase.body}</p>
                <ul className="mt-5 space-y-2">
                  {useCase.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-base md:text-lg">
                      <Check className="mt-1 size-5 shrink-0 text-primary" aria-hidden />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
                {useCase.link ? (
                  <LocaleLink
                    to={useCase.link.to}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary transition-opacity hover:opacity-80"
                  >
                    {useCase.link.label} →
                  </LocaleLink>
                ) : null}
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}
